/*
 * Sample system administration data (P0, P10) for one tenant company, and the
 * platform admin's view of all tenant companies.
 *
 * Users sign in; staff (masters.js) own work. A user is linked to at most one
 * staff record so ownership, team and supervisor come from the organisation.
 */
import { daysFromNow } from "@/helpers/crm";

// The standard roles (P0.2). A company may add its own under Roles.
export const crmRoles = ["Sales", "Sales Manager", "Customer Success", "Implementation", "Support", "Marketing", "Finance", "Management", "CTO/Admin", "Audit/Risk"];

export const dataScopes = [
  { key: "own", label: "Own", help: "Records the user owns or is assigned to" },
  { key: "team", label: "Team", help: "Records owned by anyone in the user's team" },
  { key: "department", label: "Department", help: "Records owned by anyone in the user's department" },
  { key: "all", label: "All company", help: "Every record in this company" },
  { key: "none", label: "No access", help: "Module hidden" },
];

/*
 * status: active | invited | suspended | deactivated
 * scope is the widest data scope the user's roles give (P0.4).
 */
const U = (id, name, email, phone, staff_id, roles, scope, status, mfa, last_login_ago, failed = 0, extra = {}) => ({
  id, name, email, phone, staff_id, roles, scope, status, mfa,
  last_login: last_login_ago === null ? null : `${daysFromNow(-last_login_ago)}T${String(7 + (id % 10)).padStart(2, "0")}:${String((id * 13) % 60).padStart(2, "0")}`,
  failed_attempts: failed,
  locked: failed >= 5,
  ...extra,
});

export const users = [
  U(1, "Jane Wanjiku", "jane.wanjiku@demulla.co.ke", "+254 722 410 118", 1, ["Sales Manager", "CTO/Admin"], "team", "active", true, 0),
  U(2, "Brian Otieno", "brian.otieno@demulla.co.ke", "+254 733 201 554", 2, ["Sales Manager", "Management"], "department", "active", true, 0),
  U(3, "Ann Mutua", "ann.mutua@demulla.co.ke", "+254 711 882 019", 3, ["Sales"], "own", "active", false, 1),
  U(4, "Peter Ouma", "peter.ouma@demulla.co.ke", "+254 720 556 771", 4, ["Sales"], "own", "active", false, 0, 2),
  U(5, "Lucy Chebet", "lucy.chebet@demulla.co.ke", "+254 701 330 245", 5, ["Sales"], "own", "active", false, 3),
  U(6, "Kevin Mwangi", "kevin.mwangi@demulla.co.ke", "+254 724 905 610", 6, ["Implementation"], "department", "active", true, 0),
  U(7, "Grace Njeri", "grace.njeri@demulla.co.ke", "+254 722 118 090", 7, ["Customer Success", "Management"], "department", "active", true, 0),
  U(8, "Faith Achieng", "faith.achieng@demulla.co.ke", "+254 735 640 222", 8, ["Support"], "department", "active", true, 1),
  U(9, "Dennis Kiprop", "dennis.kiprop@demulla.co.ke", "+254 710 773 418", 9, ["Marketing"], "team", "active", false, 6),
  U(10, "Samuel Kariuki", "samuel.kariuki@demulla.co.ke", "+254 729 004 561", 10, ["Customer Success"], "own", "active", false, 2, 5),
  U(11, "Mercy Wambui", "mercy.wambui@demulla.co.ke", "+254 721 667 300", 11, ["Finance", "CTO/Admin"], "all", "active", true, 0),
  U(12, "Tom Barasa", "tom.barasa@demulla.co.ke", "+254 712 448 901", 12, ["Sales"], "own", "deactivated", false, 26, 0, {
    deactivation: { type: "exit", on: daysFromNow(-25), by: "Mercy Wambui", note: "Resigned; records reassigned to Peter Ouma", reassigned_to: 4 },
  }),
  U(13, "Wanjiru Kamau", "wanjiru.kamau@demulla.co.ke", "+254 716 202 090", null, ["Audit/Risk"], "all", "active", true, 9, 0, { note: "External auditor; read-only" }),
  U(14, "Joseph Mutiso", "joseph.mutiso@demulla.co.ke", "+254 708 551 744", null, ["Support"], "own", "invited", false, null, 0, { invited_on: daysFromNow(-2) }),
  U(15, "Halima Abdi", "halima.abdi@demulla.co.ke", "+254 725 119 863", null, ["Implementation"], "own", "suspended", false, 40, 0, {
    deactivation: { type: "suspension", on: daysFromNow(-12), by: "Kevin Mwangi", note: "Disciplinary process pending" },
  }),
  U(16, "Moses Kiprono", "moses.kiprono@demulla.co.ke", "+254 703 880 412", null, ["Sales"], "own", "deactivated", false, 95, 0, {
    deactivation: { type: "transfer", on: daysFromNow(-90), by: "Brian Otieno", note: "Transferred to sister company Demulla Uganda", reassigned_to: 3 },
  }),
];

// Record-level authorization (P0.4): role × module → scope.
export const accessModules = ["Prospects", "Leads & Opportunities", "Quotations", "Customers", "Onboarding", "Support tickets", "Renewals", "Churn & Exit", "Reports"];

export const accessMatrix = {
  Sales: ["own", "own", "own", "own", "none", "own", "own", "none", "own"],
  "Sales Manager": ["team", "team", "team", "team", "team", "team", "team", "team", "team"],
  "Customer Success": ["none", "none", "none", "department", "department", "department", "department", "department", "department"],
  Implementation: ["none", "none", "none", "own", "department", "own", "none", "none", "own"],
  Support: ["none", "none", "none", "all", "none", "department", "none", "none", "own"],
  Marketing: ["all", "team", "none", "none", "none", "none", "none", "none", "department"],
  Finance: ["none", "all", "all", "all", "none", "none", "all", "all", "all"],
  Management: ["all", "all", "all", "all", "all", "all", "all", "all", "all"],
  "CTO/Admin": ["none", "none", "none", "none", "none", "none", "none", "none", "none"],
  "Audit/Risk": ["all", "all", "all", "all", "all", "all", "all", "all", "all"],
};

// Segregation of duties (P0.6).
export const sodRules = [
  { key: "no_self_approval", rule: "No self-approval", explain: "Nobody may approve a request they raised, even if their role can approve. The approval skips to the next approver.", on: true, locked: true },
  { key: "maker_checker_discount", rule: "Maker-checker: discounts", explain: "A discount above the matrix threshold is entered by one person and approved by another.", on: true, locked: false },
  { key: "maker_checker_exit", rule: "Maker-checker: customer exit / deactivation", explain: "The person requesting the exit cannot confirm the account deactivation.", on: true, locked: false },
  { key: "maker_checker_pricing", rule: "Maker-checker: price lists", explain: "Price list changes need Finance approval before the effective date.", on: true, locked: false },
  { key: "maker_checker_reassign", rule: "Maker-checker: bulk reassignment", explain: "Moving more than 10 records between owners needs a manager's approval.", on: true, locked: false },
  { key: "admin_separate", rule: "Admin role separated from commercial approval", explain: "CTO/Admin can configure the system and manage users but can never approve quotations, discounts or exits, and sees no commercial values.", on: true, locked: true },
  { key: "own_access", rule: "No granting access to yourself", explain: "Role assignments and temporary access for yourself must be approved by another administrator.", on: true, locked: true },
  { key: "audit_readonly", rule: "Audit/Risk is read-only", explain: "Audit/Risk sees everything, changes nothing.", on: true, locked: false },
];

export const makerCheckerList = [
  { action: "Quotation discount > 10%", maker: "Sales", checker: "Sales Manager", fallback: "Head of Sales" },
  { action: "Quotation value > KES 2,000,000", maker: "Sales Manager", checker: "Management", fallback: "Finance" },
  { action: "Price list change", maker: "Finance", checker: "Management", fallback: "—" },
  { action: "Customer exit", maker: "Customer Success", checker: "Management", fallback: "Finance" },
  { action: "Bulk reassignment (> 10 records)", maker: "Sales Manager", checker: "Head of Sales", fallback: "Management" },
  { action: "Role assignment / temporary access", maker: "CTO/Admin", checker: "Another CTO/Admin or Management", fallback: "—" },
];

/* Temporary access (P0.7). status: pending | active | expired | revoked */
export const temporaryAccess = [
  { id: 1, user_id: 3, grant: "Role: Sales Manager", scope: "Team · SME Desk", start: daysFromNow(-3), expiry: daysFromNow(4), reason: "Covering for Jane Wanjiku on leave", requested_by: "Brian Otieno", approver: "Mercy Wambui", status: "active" },
  { id: 2, user_id: 13, grant: "Permission: export", scope: "Reports · All company", start: daysFromNow(-1), expiry: daysFromNow(1), reason: "Quarterly revenue audit sample", requested_by: "Wanjiru Kamau", approver: "Mercy Wambui", status: "active" },
  { id: 3, user_id: 8, grant: "Record: Coastline Freight", scope: "Customer + contracts", start: daysFromNow(0), expiry: daysFromNow(14), reason: "Escalated complaint handling", requested_by: "Faith Achieng", approver: null, status: "pending" },
  { id: 4, user_id: 6, grant: "Permission: view pricing", scope: "Quotations · Rift Valley Millers", start: daysFromNow(1), expiry: daysFromNow(8), reason: "Scope implementation against the signed quotation", requested_by: "Kevin Mwangi", approver: null, status: "pending" },
  { id: 5, user_id: 10, grant: "Role: Customer Success (department)", scope: "Department", start: daysFromNow(-30), expiry: daysFromNow(-16), reason: "Grace Njeri on annual leave", requested_by: "Grace Njeri", approver: "Brian Otieno", status: "expired" },
  { id: 6, user_id: 9, grant: "Permission: export", scope: "Prospects · All company", start: daysFromNow(-20), expiry: daysFromNow(-6), reason: "Campaign list for HR Summit", requested_by: "Dennis Kiprop", approver: "Brian Otieno", status: "revoked", revoked_note: "Revoked early: export completed" },
];

/* Session & authentication policy (P0.10). */
export const securityPolicy = {
  password: { min_length: 12, upper: true, lower: true, number: true, symbol: true, expiry_days: 90, history: 5, no_name: true },
  mfa: { mode: "required_roles", roles: ["CTO/Admin", "Finance", "Management", "Audit/Risk"], methods: ["Authenticator app", "SMS code"], remember_days: 30 },
  lockout: { attempts: 5, minutes: 30, notify_admin: true },
  session: { idle_minutes: 30, absolute_hours: 12, single_session: false },
  ip: { enabled: false, ranges: ["196.201.214.0/24", "41.90.64.12"] },
  hours: { enabled: false, from: "06:00", to: "21:00", days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], exempt_roles: ["Support", "CTO/Admin"] },
};

export const signInEvents = [
  { at: `${daysFromNow(0)}T08:02`, user: "Jane Wanjiku", ip: "41.90.64.12", result: "Success (MFA)" },
  { at: `${daysFromNow(0)}T07:51`, user: "Samuel Kariuki", ip: "105.163.2.77", result: "Locked after 5 failed attempts" },
  { at: `${daysFromNow(-1)}T22:14`, user: "Unknown (admin@demulla.co.ke)", ip: "185.220.101.4", result: "Failed — unknown user" },
  { at: `${daysFromNow(-1)}T09:30`, user: "Dennis Kiprop", ip: "41.90.64.12", result: "Failed password (2)" },
  { at: `${daysFromNow(-2)}T08:15`, user: "Mercy Wambui", ip: "41.90.64.12", result: "Success (MFA)" },
];

/* ------------------------------------------------------------------ */
/* Migration, UAT and go-live (P10)                                     */
/* ------------------------------------------------------------------ */

export const migrationPlan = {
  sources: [
    { name: "Legacy CRM (Zoho export)", owner: "Jane Wanjiku", entities: "Prospects, leads, contacts" },
    { name: "Customer & contracts register (Excel)", owner: "Grace Njeri", entities: "Customers, contracts, subscriptions" },
    { name: "Support mailbox / spreadsheet", owner: "Faith Achieng", entities: "Open tickets (last 90 days)" },
  ],
  cutoff: daysFromNow(-6),
  freeze_from: daysFromNow(-5),
  freeze_to: daysFromNow(3),
  go_live: daysFromNow(5),
  owner: "Kevin Mwangi",
  sponsor: "Brian Otieno",
};

export const migrationBatches = [
  { entity: "Customers", source: 214, loaded: 212, rejected: 2, duplicates: 6, status: "validated", owner: "Grace Njeri" },
  { entity: "Contacts", source: 1186, loaded: 1149, rejected: 37, duplicates: 58, status: "validated", owner: "Grace Njeri" },
  { entity: "Prospects", source: 2410, loaded: 2302, rejected: 108, duplicates: 211, status: "reconciling", owner: "Jane Wanjiku" },
  { entity: "Leads & opportunities", source: 640, loaded: 631, rejected: 9, duplicates: 14, status: "validated", owner: "Jane Wanjiku" },
  { entity: "Contracts", source: 198, loaded: 198, rejected: 0, duplicates: 0, status: "signed_off", owner: "Mercy Wambui" },
  { entity: "Subscriptions", source: 233, loaded: 229, rejected: 4, duplicates: 0, status: "reconciling", owner: "Mercy Wambui" },
  { entity: "Open tickets", source: 87, loaded: 0, rejected: 0, duplicates: 0, status: "pending", owner: "Faith Achieng" },
];

export const cleansingIssues = [
  { type: "Duplicates", entity: "Prospects", count: 211, rule: "Same phone or email", action: "Merged 180, 31 under review", status: "in_progress" },
  { type: "Duplicates", entity: "Contacts", count: 58, rule: "Same email within a customer", action: "Merged", status: "done" },
  { type: "Invalid values", entity: "Prospects", count: 74, rule: "Industry not in master list", action: "Mapped to master values", status: "done" },
  { type: "Invalid values", entity: "Contacts", count: 37, rule: "Phone not in +254 format", action: "Reformatted 30, 7 rejected", status: "done" },
  { type: "Missing data", entity: "Subscriptions", count: 4, rule: "No renewal date", action: "Finance to supply", status: "open" },
  { type: "Missing data", entity: "Customers", count: 2, rule: "No KRA PIN (now required)", action: "CS to obtain", status: "open" },
];

export const uatChecklist = [
  { area: "Prospects", test: "Create, qualify, convert to lead", tester: "Ann Mutua", result: "pass" },
  { area: "Prospects", test: "Disqualify with reason, recycle on revisit date", tester: "Lucy Chebet", result: "pass" },
  { area: "Pipeline", test: "Move opportunity through stages; probability updates", tester: "Peter Ouma", result: "pass" },
  { area: "Pipeline", test: "Won creates customer and onboarding project", tester: "Jane Wanjiku", result: "fail" },
  { area: "Quotations", test: "Discount over 10% goes for approval", tester: "Jane Wanjiku", result: "pass" },
  { area: "Onboarding", test: "Cannot hand over without sign-off", tester: "Kevin Mwangi", result: "pass" },
  { area: "Support", test: "Ticket via API from connected app; webhook back", tester: "Faith Achieng", result: "pending" },
  { area: "Renewals", test: "Reminders at 90/60/30/7 days", tester: "Grace Njeri", result: "pass" },
  { area: "Security", test: "Sales sees only own records", tester: "Wanjiru Kamau", result: "pass" },
  { area: "Security", test: "Lockout after 5 failed attempts", tester: "Wanjiru Kamau", result: "pass" },
];

export const defects = [
  { id: "DEF-014", title: "Won opportunity does not create onboarding project when package missing", severity: "Critical", owner: "DeeCRM360 dev", status: "open", raised: daysFromNow(-2) },
  { id: "DEF-013", title: "Webhook signature header name differs from docs", severity: "High", owner: "DeeCRM360 dev", status: "fixed", raised: daysFromNow(-3) },
  { id: "DEF-011", title: "Renewal report totals exclude multi-currency contracts", severity: "Medium", owner: "DeeCRM360 dev", status: "open", raised: daysFromNow(-5) },
  { id: "DEF-009", title: "Typo on disqualify modal", severity: "Low", owner: "DeeCRM360 dev", status: "closed", raised: daysFromNow(-7) },
  { id: "DEF-007", title: "Imported prospects lose source campaign", severity: "Medium", owner: "Jane Wanjiku", status: "fixed", raised: daysFromNow(-9) },
];

export const signOffs = [
  { area: "Sales", signatory: "Brian Otieno", status: "signed", on: daysFromNow(-1), note: "Subject to DEF-014 fix" },
  { area: "Implementation", signatory: "Kevin Mwangi", status: "signed", on: daysFromNow(-2), note: "" },
  { area: "Customer Success", signatory: "Grace Njeri", status: "pending", on: null, note: "Waiting on subscription reconciliation" },
  { area: "Support", signatory: "Faith Achieng", status: "pending", on: null, note: "Ticket API UAT outstanding" },
  { area: "Management", signatory: "Mercy Wambui", status: "pending", on: null, note: "" },
];

export const goLiveChecklist = [
  { item: "All Critical and High defects closed or accepted", done: false },
  { item: "Migration reconciled and signed off per entity", done: false },
  { item: "Users created, roles assigned, MFA enrolled for required roles", done: true },
  { item: "Settings reviewed: stages, reason codes, SLA, numbering", done: true },
  { item: "Integrations tested (email, website forms, SMS)", done: true },
  { item: "Training completed for every department", done: true },
  { item: "Backup taken and rollback tested", done: true },
  { item: "Communication sent to staff and customers", done: false },
  { item: "Hypercare roster agreed", done: true },
];

export const backupPlan = {
  backup: [
    { what: "Legacy CRM full export", when: daysFromNow(-6), where: "Encrypted archive · Finance drive", by: "Jane Wanjiku" },
    { what: "DeeCRM360 tenant snapshot (pre-go-live)", when: daysFromNow(4), where: "Platform backup, retained 90 days", by: "DeeCRM360 platform" },
  ],
  rollback: [
    "Decision point: go-live day + 2 at 17:00, by Brian Otieno and Mercy Wambui",
    "Trigger: Critical defect blocking sales or support with no workaround",
    "Restore legacy CRM to read-write; freeze DeeCRM360 tenant",
    "Replay records created in DeeCRM360 since go-live into legacy (export provided)",
  ],
};

export const hypercareLog = [
  { day: 1, at: daysFromNow(6), issue: "—", severity: "—", owner: "—", status: "planned" },
];

export const postGoLiveAudit = [
  { check: "Records created by migrated users match owners in legacy", due: daysFromNow(19), owner: "Wanjiru Kamau", status: "planned" },
  { check: "No user has wider scope than their role requires", due: daysFromNow(19), owner: "Wanjiru Kamau", status: "planned" },
  { check: "Change log reviewed for go-live week", due: daysFromNow(12), owner: "Mercy Wambui", status: "planned" },
  { check: "Reconciliation re-run 30 days after go-live", due: daysFromNow(35), owner: "Kevin Mwangi", status: "planned" },
];

/* ------------------------------------------------------------------ */
/* Platform admin: tenant companies                                      */
/* ------------------------------------------------------------------ */

export const platformPlans = [
  { key: "starter", name: "Starter", users: 10, apps: 1, storage: 10, price: 15000 },
  { key: "business", name: "Business", users: 40, apps: 5, storage: 50, price: 65000 },
  { key: "enterprise", name: "Enterprise", users: 200, apps: 25, storage: 500, price: 220000 },
];

// The modules a plan includes, which the platform admin may override per tenant.
export const platformModules = [
  { key: "prospects", name: "Prospects", plans: ["starter", "business", "enterprise"] },
  { key: "leads", name: "Leads & Pipeline", plans: ["starter", "business", "enterprise"] },
  { key: "quotations", name: "Quotations", plans: ["business", "enterprise"] },
  { key: "demos", name: "Demos", plans: ["business", "enterprise"] },
  { key: "onboarding", name: "Onboarding", plans: ["business", "enterprise"] },
  { key: "success", name: "Customer Success", plans: ["starter", "business", "enterprise"] },
  { key: "support", name: "Support & Ticket API", plans: ["starter", "business", "enterprise"] },
  { key: "renewals", name: "Renewals", plans: ["starter", "business", "enterprise"] },
  { key: "churn", name: "Churn & Exit", plans: ["business", "enterprise"] },
  { key: "reports", name: "Reports", plans: ["starter", "business", "enterprise"] },
  { key: "forecast", name: "Forecasting & KPI engine", plans: ["enterprise"] },
  { key: "portal", name: "Customer portal", plans: ["enterprise"] },
  { key: "whatsapp", name: "WhatsApp channel", plans: ["enterprise"] },
];

/*
 * footprint: how far each company has moved from the default configuration —
 * the proof that tenants run different processes on the same product.
 */
export const tenants = [
  {
    id: 1, code: "CRM001", name: "Demulla Investment Ltd", industry: "Software", country: "Kenya", city: "Nairobi", status: "active", plan: "business",
    created_at: daysFromNow(-412), contact: { name: "Jane Wanjiku", email: "jane.wanjiku@demulla.co.ke", phone: "+254 722 410 118" },
    kra_pin: "P051234567X", addresses: ["crm.demulla.co.ke", "demulla.deecrm360.com"],
    overrides: { forecast: true },
    usage: { users: 34, apps: 4, api_calls: 61200, storage: 18.6, records: { Prospects: 2410, Leads: 640, Opportunities: 311, Customers: 218, Tickets: 4409, Onboarding: 46 } },
    footprint: { custom_fields: [14, 0], stages: [9, 34], reason_codes: [6, 21], lookups: [11, 96], sla: [4, 4], templates: [4, 1], notifications: [12, 17], numbering: [3, 9] },
    billing: { cycle: "Annual", amount: 780000, next_invoice: daysFromNow(214), status: "paid" },
  },
  {
    id: 2, code: "CRM002", name: "Harambee Sacco Society", industry: "SACCO", country: "Kenya", city: "Nairobi", status: "active", plan: "enterprise",
    created_at: daysFromNow(-260), contact: { name: "Philip Kiptoo", email: "pkiptoo@harambeesacco.co.ke", phone: "+254 733 990 112" },
    kra_pin: "P051998877Q", addresses: ["crm.harambeesacco.co.ke"], overrides: {},
    usage: { users: 120, apps: 2, api_calls: 18400, storage: 41, records: { Prospects: 0, Leads: 3920, Opportunities: 1102, Customers: 18450, Tickets: 912, Onboarding: 0 } },
    footprint: { custom_fields: [22, 0], stages: [14, 34], reason_codes: [12, 21], lookups: [30, 96], sla: [2, 4], templates: [0, 1], notifications: [5, 17], numbering: [9, 9] },
    billing: { cycle: "Annual", amount: 2640000, next_invoice: daysFromNow(105), status: "paid" },
  },
  {
    id: 3, code: "CRM003", name: "Pwani Logistics", industry: "Logistics", country: "Kenya", city: "Mombasa", status: "active", plan: "starter",
    created_at: daysFromNow(-90), contact: { name: "Hassan Ali", email: "hassan@pwanilogistics.co.ke", phone: "+254 722 300 451" },
    kra_pin: "P052112233L", addresses: ["pwani.deecrm360.com"], overrides: { onboarding: true },
    usage: { users: 8, apps: 1, api_calls: 2100, storage: 2.4, records: { Prospects: 310, Leads: 88, Opportunities: 41, Customers: 64, Tickets: 120, Onboarding: 3 } },
    footprint: { custom_fields: [3, 0], stages: [2, 34], reason_codes: [0, 21], lookups: [4, 96], sla: [0, 4], templates: [0, 1], notifications: [1, 17], numbering: [0, 9] },
    billing: { cycle: "Monthly", amount: 15000, next_invoice: daysFromNow(11), status: "overdue" },
  },
  {
    id: 4, code: "CRM004", name: "Kampala Retail Hub", industry: "Retail", country: "Uganda", city: "Kampala", status: "suspended", plan: "business",
    created_at: daysFromNow(-200), contact: { name: "Moses Ssali", email: "moses@krh.co.ug", phone: "+256 772 118 441" },
    kra_pin: "—", addresses: ["krh.deecrm360.com"], overrides: {},
    usage: { users: 22, apps: 0, api_calls: 0, storage: 6.1, records: { Prospects: 540, Leads: 210, Opportunities: 77, Customers: 130, Tickets: 300, Onboarding: 9 } },
    footprint: { custom_fields: [5, 0], stages: [3, 34], reason_codes: [2, 21], lookups: [9, 96], sla: [1, 4], templates: [1, 1], notifications: [0, 17], numbering: [1, 9] },
    billing: { cycle: "Monthly", amount: 65000, next_invoice: daysFromNow(-20), status: "overdue" },
  },
];

export const tenantById = (id) => tenants.find((t) => t.id === Number(id));

export const provisioningLog = [
  { at: `${daysFromNow(-412)}T10:02`, event: "Tenant created from trial application", by: "Platform admin · Otieno K." },
  { at: `${daysFromNow(-412)}T10:03`, event: "Database schema provisioned; default configuration seeded (stages, reason codes, lookups, numbering)", by: "System" },
  { at: `${daysFromNow(-412)}T10:05`, event: "Sign-in address demulla.deecrm360.com issued; first admin invited", by: "System" },
  { at: `${daysFromNow(-398)}T14:30`, event: "Custom sign-in address crm.demulla.co.ke verified (DNS)", by: "System" },
  { at: `${daysFromNow(-380)}T09:00`, event: "Plan changed Starter → Business", by: "Platform admin · Otieno K." },
  { at: `${daysFromNow(-120)}T11:20`, event: "Module override: Forecasting & KPI engine enabled (pilot)", by: "Platform admin · Achieng M." },
  { at: `${daysFromNow(-30)}T08:00`, event: "Storage quota warning sent (35%)", by: "System" },
];
